import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import DashboardToast from '../components/dashboard/DashboardToast';
import { useToast } from '../components/dashboard/hooks/useToast';
import ConfirmDeleteFileDialog from '../components/swagger/ConfirmDeleteFileDialog';
import SwaggerFileDialog, { SwaggerDialogTarget } from '../components/swagger/SwaggerFileDialog';
import SwaggerFilesHeader from '../components/swagger/SwaggerFilesHeader';
import SwaggerFilesPanel from '../components/swagger/SwaggerFilesPanel';
import { errorMessage, matchesQuery } from '../components/swagger/swaggerFileModel';
import { useSwaggerFiles } from '../components/swagger/useSwaggerFiles';
import { apiService } from '../services/apiService';
import { SwaggerFile, SwaggerFileDetails } from '../types/swaggerFile';

const SwaggerFilesPage: React.FC = () => {
  const { files, loading, loadError, reload, upload, update, remove } = useSwaggerFiles();
  const { toast, notify, dismiss } = useToast();

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [dialog, setDialog] = useState<SwaggerDialogTarget | null>(null);
  // Bumped on every open so the dialog remounts with a fresh draft.
  const [dialogKey, setDialogKey] = useState(0);
  const [newFileId, setNewFileId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SwaggerFile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const visibleFiles = useMemo(() => files.filter((f) => matchesQuery(f, deferredQuery)), [files, deferredQuery]);

  const openDialog = useCallback((target: SwaggerDialogTarget) => {
    setDialogKey((k) => k + 1);
    setDialog(target);
  }, []);
  const openUpload = useCallback(() => openDialog({ mode: 'upload' }), [openDialog]);
  const openEdit = useCallback((file: SwaggerFile) => openDialog({ mode: 'edit', file }), [openDialog]);
  const closeDialog = useCallback(() => setDialog(null), []);

  const submitDialog = useCallback(
    async (details: SwaggerFileDetails, file: File | null): Promise<boolean> => {
      if (!dialog) return false;
      try {
        if (dialog.mode === 'edit') {
          await update(dialog.file.id, details);
          notify('Changes saved');
        } else if (file) {
          const created = await upload(file, details);
          setNewFileId(created.id);
          notify('File uploaded');
        }
        setDialog(null);
        return true;
      } catch (err) {
        notify(errorMessage(err, dialog.mode === 'edit' ? 'Failed to update swagger file' : 'Failed to upload swagger file'), 'error');
        return false;
      }
    },
    [dialog, update, upload, notify],
  );

  const download = useCallback(
    async (file: SwaggerFile) => {
      setDownloadingId(file.id);
      try {
        await apiService.downloadSwaggerFile(file.id, file.originalName);
      } catch (err) {
        notify(errorMessage(err, 'Failed to download swagger file'), 'error');
      } finally {
        setDownloadingId(null);
      }
    },
    [notify],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      setPendingDelete(null);
      notify('File deleted');
    } catch (err) {
      notify(errorMessage(err, 'Failed to delete swagger file'), 'error');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, remove, notify]);
  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 1320, width: '100%' }}>
      <SwaggerFilesHeader onUpload={openUpload} />
      <SwaggerFilesPanel
        totalCount={files.length}
        visibleFiles={visibleFiles}
        loading={loading}
        loadError={loadError}
        query={query}
        newFileId={newFileId}
        downloadingId={downloadingId}
        onQueryChange={setQuery}
        onRetry={reload}
        onEdit={openEdit}
        onDownload={download}
        onDelete={setPendingDelete}
      />
      <SwaggerFileDialog key={dialogKey} target={dialog} files={files} onClose={closeDialog} onSubmit={submitDialog} />
      <ConfirmDeleteFileDialog file={pendingDelete} deleting={deleting} onCancel={cancelDelete} onConfirm={confirmDelete} />
      <DashboardToast toast={toast} onClose={dismiss} />
    </Box>
  );
};

export default SwaggerFilesPage;
